import React, { useContext, useState, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { TranslationsContext } from "../../utils/TranslationsContext";
import { images } from "../../constants";
import "./style/style.css";
import AccreditationInput from "../../components/AccreditationInput/AccreditationInput";
import AccreditationButton from "../../components/AccreditationButton/AccreditationButton";
import { createData } from "../../utils/methods";
import { apiRoutes } from "../../utils/apiRoutes";
import { useAlert } from "../../utils/AlertContext";
import { useNavigate } from "react-router-dom";
import Loader from "../../components/Loader/Loader";
import PasswordRequirements from "../../components/PasswordRequirements/PasswordRequirements";

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{12,}$/;
// Minimum 12 characters, at least one letter, one number and one special character

const ForgottenPasswordPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useContext(TranslationsContext);
  const nextButtonRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const tokenFromUrl = searchParams.get("token") || id;

  const [credentials, setCredentials] = useState({
    token: tokenFromUrl || "",
    new_password: "",
    confirmed_password: "",
  });

  const [errors, setErrors] = useState({
    token: false,
    new_password: false,
    confirmed_password: false,
  });

  const [messages, setMessages] = useState({
    token: "",
    new_password: "",
    confirmed_password: "",
    general: "",
  });

  const handleInputChange = (field, value) => {
    setCredentials((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: false }));
    setMessages((prev) => ({ ...prev, [field]: "", general: "" }));
  };

  const validateAllFields = () => {
    const { token, new_password, confirmed_password } = credentials;

    let nextErrors = {
      token: false,
      new_password: false,
      confirmed_password: false,
    };
    let nextMessages = {
      token: "",
      new_password: "",
      confirmed_password: "",
      general: "",
    };

    if (!token) {
      nextErrors.token = true;
      nextMessages.token = "Token is required.";
    }

    if (!new_password) {
      nextErrors.new_password = true;
      nextMessages.new_password = t("UI_NEW_PASSWORD_IS_REQUIRED");
    } else if (!PASSWORD_REGEX.test(new_password)) {
      nextErrors.new_password = true;
      nextMessages.new_password = t("UI_PASSWORD_MUST_MEET_REQUIREMENTS");
    }

    if (!confirmed_password) {
      nextErrors.confirmed_password = true;
      nextMessages.confirmed_password = t(
        "UI_PLEASE_CONFIRM_YOUR_NEW_PASSWORD",
      );
    }

    if (
      new_password &&
      confirmed_password &&
      new_password !== confirmed_password
    ) {
      nextErrors.confirmed_password = true;
      nextMessages.confirmed_password = t("UI_PASSWORDS_DO_NOT_MATCH");
    }

    setErrors(nextErrors);
    setMessages(nextMessages);

    const hasError = Object.values(nextErrors).some(Boolean);
    return !hasError;
  };

  const handleChangePassword = async () => {
    const isValid = validateAllFields();
    if (!isValid) return;

    const sendData = {
      token: credentials.token,
      password: credentials.new_password,
    };

    setIsLoading(true);

    try {
      const result = await createData(
        apiRoutes.newPassword,
        sendData,
        showAlert,
        navigate,
        t,
      );

      if (!result) return;

      console.log("Podaci su uspešno sačuvani:", result.data);
      showAlert(t("SUCCESSFULLY_SAVED"), "success");
      navigate("/login");
    } catch (error) {
      console.error("API poziv nije uspeo:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="flex-direction-column items-center gap-120 width-100 relative forgotten-password-page">
      <div className="flex-direction-row gap-18 items-center">
        <img src={images.logo} alt="Logo" className="flag" />
        <h1 className="font-36 inria-sans-300 login-title">
          {t("UI_FORGOTTEN_PASSWORD")}
        </h1>
      </div>

      <div className="flex-direction-column gap-24 forgotten-password-form">
        <div className="flex-direction-column gap-24 width-100">
          <div className="flex-direction-column gap-8">
            <AccreditationInput
              placeholder="Token"
              className={`change-password ${errors.token ? "error" : ""}`}
              setInputValue={(value) => handleInputChange("token", value)}
              value={credentials.token}
              label={t("TOKEN_FROM_EMAIL")}
              type="text"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  nextButtonRef.current?.click();
                }
              }}
              autoComplete="one-time-code"
            />
            {messages.token ? (
              <p className="input-error-text">{messages.token}</p>
            ) : null}
          </div>

          <div className="flex-direction-column gap-8">
            <AccreditationInput
              placeholder="*********"
              className={`change-password ${
                errors.new_password ? "error" : ""
              }`}
              setInputValue={(value) =>
                handleInputChange("new_password", value)
              }
              value={credentials.new_password}
              label={t("NEW_PASSWORD")}
              type="password"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  nextButtonRef.current?.click();
                }
              }}
              autoComplete="new-password"
            />
            {/* Once they've typed something, the requirements box below
                already shows exactly what's missing per-rule — the generic
                message is only useful for the empty-field case. */}
            {messages.new_password && !credentials.new_password ? (
              <p className="input-error-text">{messages.new_password}</p>
            ) : null}
            <PasswordRequirements password={credentials.new_password} t={t} />
          </div>

          <div className="flex-direction-column gap-8">
            <AccreditationInput
              placeholder="*********"
              className={`change-password ${
                errors.confirmed_password ? "error" : ""
              }`}
              setInputValue={(value) =>
                handleInputChange("confirmed_password", value)
              }
              value={credentials.confirmed_password}
              label={t("CONFIRM_PASSWORD")}
              type="password"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  nextButtonRef.current?.click();
                }
              }}
              autoComplete="new-password"
            />
            {messages.confirmed_password ? (
              <p className="input-error-text">{messages.confirmed_password}</p>
            ) : null}
          </div>

          {messages.general ? (
            <p className="input-error-text">{messages.general}</p>
          ) : null}
        </div>

        <AccreditationButton
          type="save"
          title={t("BTN_SAVE")}
          className="login-btn"
          onClick={handleChangePassword}
          ref={nextButtonRef}
        />
      </div>
    </div>
  );
};

export default ForgottenPasswordPage;
